import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { toE164 } from '../lib/utils';
import { requestOtp, verifyOtpCode, isFreshlyVerified } from '../lib/otp';

// Role permissions for route access
export const ROUTE_PERMISSIONS = {
  '/admin': ['media', 'marketing', 'admin', 'super-admin'],
  '/investor': ['investor', 'admin', 'super-admin'],
  '/portal': ['customer', 'admin', 'super-admin'],
  '/download': ['super-admin'],
};

// Create auth context
const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from database
  const fetchProfile = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }

      setProfile(data);
      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Keep loading=true while we refetch the profile so role-gated
        // redirects don't evaluate a null role (transient "Access Denied").
        setLoading(true);
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  // Sign in with phone + password
  const signIn = async (phone, password) => {
    try {
      const e164Phone = toE164(phone);
      const { data, error } = await supabase.auth.signInWithPassword({
        phone: e164Phone,
        password,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Send OTP via SMS. Defaults to shouldCreateUser:false so login / password-reset
  // flows don't silently create ghost users for numbers that never registered.
  // Register passes shouldCreateUser:true for genuine signup.
  // Delivered by Message Central — see lib/otp.js.
  const sendOtp = async (phone, { shouldCreateUser = false } = {}) => {
    try {
      await requestOtp(toE164(phone), shouldCreateUser);
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  // Verify OTP
  const verifyOtp = async (phone, token, { shouldCreateUser = false } = {}) => {
    try {
      const data = await verifyOtpCode(toE164(phone), token, shouldCreateUser);
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Register new customer account with phone + OTP
  const signUp = async (phone, otpToken, password, metadata = {}) => {
    try {
      const e164Phone = toE164(phone);

      // Verify OTP first — this creates the auth user + session
      const verifyData = (await isFreshlyVerified(e164Phone))
        ? (await supabase.auth.getSession()).data.session
        : await verifyOtpCode(e164Phone, otpToken, true);

      // Set password
      const { error: pwError } = await supabase.auth.updateUser({ password });
      if (pwError) throw pwError;

      // Create/update profile
      const currentUser = verifyData.user;
      if (currentUser) {
        const { error: profileError } = await supabase.from('profiles').upsert({
          id: currentUser.id,
          full_name: metadata.fullName || '',
          phone: e164Phone,
          role: 'customer',
          active_account: true,
        });
        if (profileError) throw profileError;
      }

      return { data: verifyData, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // Reset password with OTP
  const resetPasswordWithOtp = async (phone, token, newPassword) => {
    try {
      const e164Phone = toE164(phone);

      // Verify OTP first (skipped when the OTP step already verified this phone)
      if (!(await isFreshlyVerified(e164Phone))) await verifyOtpCode(e164Phone, token, false);

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (updateError) throw updateError;

      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  // Sign out
  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
  };

  // Refresh profile data
  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  // Check if user has required role
  const hasRole = (requiredRoles) => {
    if (!profile?.role) return false;
    if (Array.isArray(requiredRoles)) {
      return requiredRoles.includes(profile.role);
    }
    return profile.role === requiredRoles;
  };

  // Check if user can access a specific route
  const canAccessRoute = (path) => {
    // Find matching route permission
    const matchingRoute = Object.keys(ROUTE_PERMISSIONS).find(route =>
      path.startsWith(route)
    );

    if (!matchingRoute) return true; // Public route

    const requiredRoles = ROUTE_PERMISSIONS[matchingRoute];
    return hasRole(requiredRoles);
  };

  // Derived auth states
  const isAuthenticated = !!session;
  const isMedia = profile?.role === 'media' || profile?.role === 'marketing';
  const isInvestor = profile?.role === 'investor';
  const isCustomer = profile?.role === 'customer';
  const isAdmin = profile?.role === 'admin' || profile?.role === 'super-admin';
  const isEmployee = profile?.is_employee === true;

  const value = {
    // State
    session,
    user,
    profile,
    loading,

    // Auth methods
    signIn,
    signUp,
    sendOtp,
    verifyOtp,
    resetPasswordWithOtp,
    signOut,
    refreshProfile,

    // Permission checks
    hasRole,
    canAccessRoute,

    // Derived states
    isAuthenticated,
    isMedia,
    isInvestor,
    isCustomer,
    isAdmin,
    isEmployee,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
